import clsx from 'clsx';
import Icon from 'components/Icon';
import {IconType} from 'components/Icon/types';
import Overlay from 'components/Layout/Overlay';
import {useTooltip} from 'components/Tooltip/hook';
import useKeyListener from 'hooks/useKeyListener';
import useOutsideDetector from 'hooks/useOutsideDetector';
import {FC, ReactNode, useRef, useState} from 'react';
import {Size} from 'types';

import DropdownActionButton from './DropdownActionButton';
import DropdownActionContainer from './DropdownActionContainer';
import Divider from './DropdownActionDivider';
import DropdownActionLink from './DropdownActionLink';
import {destructive, primary, secondary, tertiary} from './styles.module.css';

export type Kind = 'destructive' | 'primary' | 'secondary' | 'tertiary';

export const SIZES: Record<Size, string> = {
    base: 'text-base px-3 h-10',
    lg: 'text-lg px-4 h-11',
    sm: 'text-sm px-2 h-8',
    xl: 'text-xl px-4 h-12',
    xs: 'text-sm px-1.5 h-6',
};

export const ICON_ONLY_SIZES: Record<Size, string> = {
    base: 'py-3 px-3',
    lg: 'py-4 px-4',
    sm: 'py-2 px-2',
    xl: 'py-4 px-4',
    xs: 'py-1.5 px-1.5',
};

export const KINDS: Record<Kind, string> = {
    destructive,
    primary,
    secondary,
    tertiary,
};

export const ICON_GAP = {
    base: 'gap-2',
    lg: 'gap-2',
    sm: 'gap-2',
    xl: 'gap-2',
    xs: 'gap-1.5',
};

export const BASE_ICON = ['xs', 'sm', 'base', 'lg'];

export type DropdownFC = {
    Button: typeof DropdownActionButton;
    Divider: typeof Divider;
    Link: typeof DropdownActionLink;
} & FC<DropdownProps>;

export type DropdownProps = {
    ariaLabel?: string;
    children?: ReactNode;
    classNameAction?: string;
    classNameContainer?: string;
    classNameIcon?: string;
    classNameMenu?: string;
    disabled?: boolean;
    hasCaret?: boolean;
    icon?: IconType;
    kind?: Kind;
    label: string;
    layoutOffset?: number;
    size?: Size;
};

const DropdownAction: DropdownFC = ({
    ariaLabel,
    children,
    classNameAction,
    classNameContainer,
    classNameIcon,
    classNameMenu,
    disabled,
    hasCaret = true,
    icon,
    kind = 'primary',
    label,
    layoutOffset,
    size = 'base',
}) => {
    const [show, setShow] = useState(() => false);

    const refAction = useRef<HTMLDivElement>(null);
    const refPortal = useRef<HTMLDivElement>(null);
    const {setTooltipRef} = useTooltip();

    useOutsideDetector([refAction, refPortal], () => setShow(false));
    useKeyListener(['Escape', 'Tab'], () => setShow(false));

    const iconComponent = icon ? (
        <Icon
            className={clsx(children && 'flex-none', classNameIcon)}
            icon={icon}
            size={BASE_ICON.includes(size) ? 'base' : 'lg'}
        />
    ) : null;

    const arrowComponent = hasCaret && (
        <Icon
            className={clsx(children && 'flex-none', classNameIcon)}
            icon={show ? 'small_arrow_up' : 'small_arrow_down'}
            size={BASE_ICON.includes(size) ? 'base' : 'lg'}
        />
    );

    return (
        <>
            <div
                aria-expanded={show}
                aria-label={ariaLabel}
                className={clsx(
                    'flex select-none items-center whitespace-nowrap rounded border',
                    ICON_GAP[size],
                    KINDS[kind],
                    iconComponent && !label && !arrowComponent
                        ? ICON_ONLY_SIZES[size]
                        : SIZES[size],
                    classNameAction,
                )}
                data-disabled={disabled}
                data-dropdown-action={true}
                onClick={() => !disabled && setShow((prevState) => !prevState)}
                onKeyDown={(event) =>
                    !disabled &&
                    event.key === 'Enter' &&
                    setShow((prevState) => !prevState)
                }
                ref={disabled ? (ref) => setTooltipRef(ref) : refAction}
                role="button"
                tabIndex={0}
            >
                {iconComponent}
                {label}
                {arrowComponent}
            </div>
            {show && refAction.current && (
                <Overlay
                    className={classNameMenu}
                    element={refAction.current}
                    layoutLayer="z-dropdown"
                    layoutMethod="bottom-left-or-top-right"
                    layoutOffset={layoutOffset}
                    onClick={() => setShow((prevState) => !prevState)}
                >
                    <DropdownActionContainer
                        className={classNameContainer}
                        ref={refPortal}
                    >
                        {children}
                    </DropdownActionContainer>
                </Overlay>
            )}
        </>
    );
};

DropdownAction.Divider = Divider;
DropdownAction.Button = DropdownActionButton;
DropdownAction.Link = DropdownActionLink;

export default DropdownAction;
